import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, formatGBP, ONBOARDING_DRAFT_KEY } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import WhatIfSlider from "@/components/WhatIfSlider";
import InfoTooltip from "@/components/InfoTooltip";
import ForecastStorySnapshot from "@/components/ForecastStorySnapshot";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import {
  Compass,
  Coins,
  TrendUp,
  WarningCircle,
  ArrowRight,
  Calendar,
  ArrowCounterClockwise,
  Target,
  CheckSquare,
  VideoCamera,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// Always show "Cash Right Now (MONTH)" with the bigger of (today's calendar month) and
// (the month at index 0 of the forecast). i.e. if forecast starts June and today is May,
// show "Cash Right Now (June)" because that's the cash on day-1 of the forecast horizon.
function cashRightNowLabel(monthsPreview) {
  if (!monthsPreview || !monthsPreview.length) return "";
  const todayIso = new Date().toISOString().slice(0, 7); // "YYYY-MM"
  // months_preview labels look like "Jun 2026" — convert each to YYYY-MM by walking labels
  // We compare by comparing real-time today vs forecast start month index 0.
  const m0Label = monthsPreview[0].label; // "Jun 2026"
  const m0Iso = labelToIso(m0Label);
  if (!m0Iso) return m0Label;
  return todayIso > m0Iso ? labelFromIso(todayIso) : m0Label;
}

function nextMonthLabel(monthsPreview) {
  if (!monthsPreview || !monthsPreview.length) return "";
  const todayIso = new Date().toISOString().slice(0, 7);
  const m0Iso = labelToIso(monthsPreview[0].label);
  if (!m0Iso) return monthsPreview[1]?.label || "";
  // If today is before forecast start, "next" is forecast month 0
  if (todayIso < m0Iso) return monthsPreview[0].label;
  // Else pick the first month strictly after today within the preview
  for (const m of monthsPreview) {
    const iso = labelToIso(m.label);
    if (iso && iso > todayIso) return m.label;
  }
  return monthsPreview[monthsPreview.length - 1].label;
}

function findMonthByLabel(months, label) {
  return months.find((m) => m.label === label);
}

const SHORT_TO_NUM = {
  Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
  Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
};
function labelToIso(label) {
  if (!label) return "";
  const [s, y] = label.split(" ");
  if (!s || !y || !SHORT_TO_NUM[s]) return "";
  return `${y}-${SHORT_TO_NUM[s]}`;
}
function labelFromIso(iso) {
  const [y, m] = iso.split("-");
  const idx = Number(m) - 1;
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[idx]} ${y}`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasDraft, setHasDraft] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  // Iter35d/e — Dashboard banners
  const [nextEvent, setNextEvent] = useState(null);
  const [cashAlert, setCashAlert] = useState(null);
  const [alertsDismissed, setAlertsDismissed] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const [dashRes, goalsRes] = await Promise.all([
          axios.get(`${API}/dashboard`),
          axios.get(`${API}/goals`).catch(() => ({ data: [] })),
        ]);
        setData(dashRes.data);
        setGoals(Array.isArray(goalsRes.data) ? goalsRes.data : []);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    }
    load();
    // Iter35d/e — parallel-fetch banner data
    axios.get(`${API}/events/next`).then((r) => {
      // Server returns {event, hours_away} — keep both
      if (r.data?.event) setNextEvent(r.data);
    }).catch(() => {});
    axios.get(`${API}/alerts/cashflow`).then((r) => {
      if (r.data?.has_alert) setCashAlert(r.data);
    }).catch(() => {});
    axios.get(`${API}/onboarding/draft`).then((r) => {
      if ((r.data?.draft?.idx || 0) > 0) setHasDraft(true);
    }).catch(() => {});
    // If arriving from the Goals page Big Decision "Show me the impact" CTA,
    // scroll straight to the What-If slider so the modelling banner is visible.
    if (typeof window !== "undefined" && window.location.search.includes("modelling=1")) {
      setTimeout(() => {
        const el = document.querySelector('[data-testid="whatif-card"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 700);
    }
    // Hash-scroll — e.g. from `/dashboard#forecast-story` (after saving a
    // weekly pulse). Small delay for the card to mount.
    if (typeof window !== "undefined" && window.location.hash) {
      setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 700);
    }
  }, [user?.email]);

  // Iter21: trial + subscription + annual all have full edit access.
  // Only cancelled/read_only/soft_deleted/expired/one_off are gated in the Dashboard UI.
  const lockedPlan = ["one_off", "cancelled", "read_only", "soft_deleted", "expired", "none"].includes(user?.plan);
  const isCancelled = user?.plan === "cancelled" || user?.plan === "read_only" || user?.plan === "soft_deleted";

  const startAgain = async () => {
    setResetting(true);
    try {
      await axios.delete(`${API}/forecast`);
      try {
        localStorage.removeItem(ONBOARDING_DRAFT_KEY);
      } catch {}
      toast.success("Forecast cleared. Let's build a new one.");
      nav("/onboarding", { replace: true });
    } catch {
      toast.error("Could not reset. Try again.");
    } finally {
      setResetting(false);
      setConfirmReset(false);
    }
  };

  if (loading) {
    return <div className="font-heading uppercase text-2xl">Loading your numbers…</div>;
  }

  if (!data?.has_forecast) {
    return (
      <div className="max-w-3xl">
        <div className="section-eyebrow">Welcome back</div>
        <h1
          className="font-heading uppercase text-4xl md:text-6xl tracking-wide mt-4 leading-[1.05]"
          data-testid="dashboard-empty-heading"
        >
          Let's build your first forecast.
        </h1>
        <p className="mt-4 text-lg text-black/75 leading-relaxed">
          Ten minutes, a few easy questions, and you'll have a dedicated 12-month plan for your
          business. It is that simple.
        </p>
        {hasDraft && (
          <div className="mt-6 ai-insight">
            <strong>You have a saved draft.</strong> Pick up where you left off — your answers are
            still there.
          </div>
        )}
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/onboarding" className="btn-primary" data-testid="dashboard-start-btn">
            <span className="hidden sm:inline">{hasDraft ? "Resume building your forecast" : "Start building your forecast"}</span>
            <span className="sm:hidden">{hasDraft ? "Keep building" : "Build your forecast"}</span>{" "}
            <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </div>
    );
  }

  const {
    current_cash,
    runway_months,
    next_month_income,
    next_month_costs_total,
    variance_score_pct,
    months_preview,
  } = data;

  const cashLabelMonth = cashRightNowLabel(months_preview);
  const upcomingLabel = nextMonthLabel(months_preview);
  const upcomingMonth = findMonthByLabel(months_preview, upcomingLabel);
  // Money in / out for the upcoming month (preferred — gives the user the breakdown
  // requested instead of just a single "costs" figure).
  const upcomingMoneyIn = upcomingMonth?.total_income ?? next_month_income ?? 0;
  const upcomingMoneyOut = upcomingMonth
    ? (upcomingMonth.total_costs || 0) + (upcomingMonth.personal_draw || 0)
    : next_month_costs_total ?? 0;
  const upcomingNet = upcomingMoneyIn - upcomingMoneyOut;

  const chartData = months_preview.map((m) => ({
    label: m.label.split(" ")[0],
    income: m.total_income,
    costs: m.total_costs + m.personal_draw,
    cash: m.cash_balance,
    net: m.net,
  }));

  // Opening cash BEFORE the first preview month = cash_balance of m0 minus its own net.
  // Used as the starting point for the What-If sensitivity slider so its cash chain
  // walks from the same baseline as the real forecast.
  const openingCashForWhatIf =
    months_preview.length > 0
      ? (months_preview[0].cash_balance || 0) - (months_preview[0].net || 0)
      : 0;

  return (
    <div className="space-y-10">
      {/* Iter35d/e — Dashboard banners: cashflow alert (red) + next event (yellow) */}
      {!alertsDismissed && cashAlert && cashAlert.has_alert && (
        <div
          className="border-2 border-red-700 bg-red-50 rounded-xl p-4 flex items-start gap-3 shadow-[4px_4px_0_#000]"
          data-testid="dashboard-cashflow-alert"
        >
          <WarningCircle size={24} weight="fill" className="text-red-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-heading uppercase text-sm text-red-800 tracking-wider">
              Cashflow alert
            </div>
            <p className="text-sm text-red-900 mt-1 leading-relaxed">
              {cashAlert.headline}{" "}
              <Link to="/actuals" className="underline font-semibold">
                Log your actuals →
              </Link>
            </p>
          </div>
          <button
            type="button"
            onClick={() => setAlertsDismissed(true)}
            className="text-red-700 hover:text-red-900 text-xs font-semibold"
            data-testid="dashboard-cashflow-alert-dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {nextEvent && (
        <div
          className="card-yellow flex items-start gap-3"
          data-testid="dashboard-event-banner"
        >
          <VideoCamera size={24} weight="duotone" className="shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-heading uppercase text-sm tracking-wider">
              Coming up soon · {Math.round(nextEvent.hours_away || 0)}h away
            </div>
            <p className="text-sm mt-1 leading-relaxed">
              <strong>{nextEvent.event.title}</strong> ·{" "}
              {new Date(nextEvent.event.starts_at).toLocaleString("en-GB", {
                weekday: "short",
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              ·{" "}
              <Link to="/community" className="underline font-semibold">
                See details →
              </Link>
            </p>
          </div>
        </div>
      )}

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="section-eyebrow">Dashboard</div>
          <h1
            className="font-heading uppercase text-4xl md:text-6xl tracking-wide mt-4 leading-[1.05]"
            data-testid="dashboard-heading"
          >
            Hello {user?.name?.split(" ")[0] || "there"}. <br />
            <span className="highlight-yellow inline-block mt-2">Here's where you stand.</span>
          </h1>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setConfirmReset(true)}
          data-testid="start-again-btn"
        >
          <ArrowCounterClockwise size={16} weight="bold" /> Start again from scratch
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Coins}
          label={`Cash this month${cashLabelMonth ? ` (${cashLabelMonth.split(" ")[0]})` : ""}`}
          value={formatGBP(current_cash)}
          testid="stat-current-cash"
        />
        <StatCard
          icon={Calendar}
          label="Runway"
          value={
            runway_months === null
              ? "12+ months"
              : `${runway_months} ${runway_months === 1 ? "month" : "months"}`
          }
          sub={runway_months === null ? "You're fine for the full year" : "Before cash dips below zero"}
          warn={runway_months !== null && runway_months < 3}
          testid="stat-runway"
          tooltip={<>Runway is the number of months your current cash pile will last if nothing changes. Below 3 months = time to look at your inputs.</>}
        />
        <StatCard
          icon={TrendUp}
          label={`Forecasted net income${upcomingLabel ? ` (${upcomingLabel.split(" ")[0]})` : ""}`}
          value={formatGBP(upcomingNet)}
          sub={
            <span>
              Money in <strong>{formatGBP(upcomingMoneyIn)}</strong>
              <br />
              Money out <strong>{formatGBP(upcomingMoneyOut)}</strong>
            </span>
          }
          testid="stat-next-quarter"
        />
        <StatCard
          icon={
            lockedPlan
              ? Compass
              : variance_score_pct === null
              ? Compass
              : variance_score_pct >= 0
              ? TrendUp
              : WarningCircle
          }
          label="Variance health score"
          value={
            lockedPlan
              ? "—"
              : variance_score_pct === null
              ? "—"
              : `${variance_score_pct > 0 ? "+" : ""}${variance_score_pct}%`
          }
          tooltip={
            <>
              The Variance Health Score compares your logged actuals against your forecast for the same
              month. Positive % = you beat your plan; negative = you undershot. Consistent small variances
              are a sign of a well-tuned forecast.
            </>
          }
          sub={
            lockedPlan ? (
              <span>
                {isCancelled ? "Subscription paused. " : "Subscription only. "}
                <Link to="/upgrade" className="underline font-semibold" data-testid="stat-variance-upgrade">
                  {isCancelled ? "Reactivate" : "Upgrade"}
                </Link>{" "}
                to track variance.
              </span>
            ) : variance_score_pct === null ? (
              "Add actuals to see it"
            ) : variance_score_pct === 0 ? (
              "Spot on! Your actuals were the same as your forecasted figures."
            ) : variance_score_pct > 0 ? (
              "You've done better than you forecasted this month. Well done. Keep going!"
            ) : (
              "Ah this month didn't quite go as planned. That's okay. Check what can be done over on the Actuals page insights."
            )
          }
          testid="stat-variance"
        />
      </div>

      <ForecastStorySnapshot />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <div className="flex items-baseline justify-between">
          <h2 className="font-heading uppercase text-2xl">Cash balance · next 12 months</h2>
          <Link to="/forecast" className="text-sm underline font-semibold" data-testid="dashboard-to-forecast">
            See full forecast →
          </Link>
        </div>
        <div className="mt-6 h-72 min-h-[240px] w-full">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%" minHeight={240} debounce={50}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                <XAxis dataKey="label" stroke="#000" tick={{ fontFamily: "Open Sans", fontSize: 12 }} />
                <YAxis
                  stroke="#000"
                  tick={{ fontFamily: "Open Sans", fontSize: 12 }}
                  tickFormatter={(v) => `£${(v / 1000).toFixed(1)}k`}
                />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1.5px solid #000", borderRadius: 12 }}
                  formatter={(v) => formatGBP(v)}
                />
                <ReferenceLine y={0} stroke="#000" strokeWidth={1.5} />
                <Line
                  type="monotone"
                  dataKey="cash"
                  stroke="#000"
                  strokeWidth={2.5}
                  dot={{ fill: "#FFBD4A", stroke: "#000", strokeWidth: 1.5, r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-black/50 text-sm">
              No forecast data yet — save your forecast to see the cash chart.
            </div>
          )}
        </div>
      </motion.div>

      <GoalsAndActionSummary goals={goals} />

      <WhatIfSlider months={months_preview} openingCash={openingCashForWhatIf} />

      <div className="card-yellow" data-testid="dashboard-whats-next">
        <h2 className="font-heading uppercase text-2xl">What's next</h2>
        <p className="mt-3 leading-relaxed">
          Each month, pop in your actual income and costs. We'll show you what the variance is
          really saying — and a gentle pivot for next month. Or set another goal next.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/actuals" className="btn-secondary" data-testid="dashboard-to-actuals">
            Add this month's actuals <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/goals" className="btn-secondary" data-testid="dashboard-to-goals">
            Set a goal
          </Link>
        </div>
      </div>

      {confirmReset && (
        <div
          className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4"
          data-testid="confirm-reset-modal"
        >
          <div className="bg-white border-[1.5px] border-black rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-heading uppercase text-2xl">Are you sure?</h3>
            <p className="mt-3 text-black/75 leading-relaxed">
              This will delete your current forecast, all the actuals you've logged, and your
              goals. There's no undo. Once you confirm, building your forecast starts fresh.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                className="btn-primary"
                onClick={startAgain}
                disabled={resetting}
                data-testid="confirm-reset-yes"
              >
                {resetting ? "Wiping…" : "Yes, please continue"}
              </button>
              <button
                className="btn-secondary"
                onClick={() => setConfirmReset(false)}
                disabled={resetting}
                data-testid="confirm-reset-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, warn, testid, tooltip }) {
  return (
    <div className={`card ${warn ? "card-yellow" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon size={26} weight="duotone" />
        {warn && <WarningCircle size={20} weight="fill" />}
      </div>
      <div className="text-xs uppercase tracking-wide text-black/55 mt-3 flex items-center gap-1">
        <span>{label}</span>
        {tooltip && <InfoTooltip label={label}>{tooltip}</InfoTooltip>}
      </div>
      <div className="font-heading text-3xl mt-1">{value}</div>
      {sub && <div className="text-sm text-black/65 mt-1 leading-snug">{sub}</div>}
    </div>
  );
}

// Iter28: Compact "Your Top Actions" (wider left) + "Goals in flight" (narrower right).
// Left column shows up to 3 action tiles (one per goal, its first unticked action).
// Right column shows the compact goal-progress strip.
function GoalsAndActionSummary({ goals }) {
  if (!goals || goals.length === 0) {
    return (
      <div className="card flex items-center justify-between gap-4 flex-wrap" data-testid="dashboard-goals-empty">
        <div className="flex items-start gap-3">
          <Target size={22} weight="duotone" className="mt-1" />
          <div>
            <div className="font-heading uppercase text-lg">No goals yet</div>
            <p className="text-sm text-black/65 max-w-md">
              A forecast without a goal is just a spreadsheet. Set one and we'll build you a
              tickable AI action plan.
            </p>
          </div>
        </div>
        <Link to="/goals" className="btn-primary" data-testid="dashboard-goals-cta">
          Set your first goal <ArrowRight size={16} weight="bold" />
        </Link>
      </div>
    );
  }

  // Pin up to 3 most recent goals.
  const pinned = goals.slice(0, 3);
  // For each pinned goal, find the first unticked action (if any).
  const topActions = pinned.map((g) => {
    const first = (g.actions || []).find((a) => !a.completed);
    return { goal: g, action: first || null };
  });
  const anyAction = topActions.some((t) => t.action);

  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="dashboard-goals-strip">
      {/* Left / wider column — top action per goal */}
      <div className="md:col-span-2 card bg-[#FFF7E5] border-2 border-black">
        <div className="flex items-center gap-2 mb-4">
          <CheckSquare size={20} weight="duotone" />
          <div className="font-heading uppercase text-lg">Your top actions</div>
        </div>
        {!anyAction ? (
          <p className="text-sm text-black/70 leading-relaxed">
            No open actions right now. Generate an action plan on any goal to see your next moves
            surface here.
          </p>
        ) : (
          <div className="space-y-3">
            {topActions.map(({ goal, action }) => (
              <div
                key={goal.id}
                className="border border-black/15 bg-white rounded-xl p-3"
                data-testid={`dashboard-top-action-tile-${goal.id}`}
              >
                <div className="text-[10px] uppercase tracking-widest text-black/55 font-semibold mb-1">
                  For: {goal.title}
                </div>
                {action ? (
                  <>
                    <p className="text-sm leading-relaxed text-black/85">{action.text}</p>
                    <Link
                      to="/goals"
                      className="inline-flex items-center gap-1 text-xs font-semibold underline mt-2"
                      data-testid={`dashboard-top-action-cta-${goal.id}`}
                    >
                      Tick it off → <ArrowRight size={12} weight="bold" />
                    </Link>
                  </>
                ) : (
                  <p className="text-xs text-black/55 leading-relaxed">
                    {(goal.actions || []).length > 0
                      ? "All actions ticked — regenerate for the next set."
                      : goal.kind === "big_decision"
                      ? "Run the numbers on this to generate a plan."
                      : "Generate an action plan to get started."}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right / narrower column — goal progress strip */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Target size={18} weight="duotone" />
            <div className="font-heading uppercase text-base">Goals in flight</div>
          </div>
          <Link to="/goals" className="text-xs underline font-semibold" data-testid="dashboard-goals-link">
            See all →
          </Link>
        </div>
        <div className="space-y-2.5">
          {pinned.map((g) => {
            const total = (g.actions || []).length;
            const done = (g.actions || []).filter((a) => a.completed).length;
            const pct = total === 0 ? 0 : Math.round((done / total) * 100);
            return (
              <Link
                key={g.id}
                to="/goals"
                className="block border border-black/15 rounded-lg p-2.5 hover:bg-[#FFF7E5] transition-colors"
                data-testid={`dashboard-goal-tile-${g.id}`}
              >
                <div className="font-semibold text-xs truncate">{g.title}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-black/10 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#FFBD4A]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-black/55 font-semibold shrink-0">{pct}%</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
